'use client'

import { useMemo, useState } from 'react'
import {
  IconArrowNarrowLeft,
  IconCategory,
  IconChevronRight,
  IconMenu2
} from '@tabler/icons-react'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'

import {
  FetchUpsellingResponse,
  getProperProductForm,
  NavMenuCategory
} from 'modules/products'
import Button from 'shared/ui/Button'
import { Drawer, DrawerTitle } from 'shared/ui/Drawer'
import { cssColors } from 'shared/ui/utils/cssTokens'
import { theme } from 'shared/ui/utils/designTokens'
import { PATHS } from 'shared/utils/paths'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import { findMainCategory, isActive } from '../../utils'

import styles from './CategoriesNavigationMobile.module.scss'

const CategoriesNavigationMobile = ({
  categories
}: {
  categories: NavMenuCategory[]
  menuPromotionItems: FetchUpsellingResponse['data'] | null
}) => {
  const [open, setOpen] = useState(false)
  const [previusSelectedCategories, setPreviousSelectedCategories] = useState<
    (NavMenuCategory | null)[]
  >([null])
  const [selectedCategory, setSelectedCategory] =
    useState<NavMenuCategory | null>(null)

  const router = useRouter()
  const pathname = usePathname()

  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    setOpen(false)
    setSelectedCategory(null)
  }

  const handleCategoryClick = (category: NavMenuCategory) => () => {
    if (isNotNilOrEmpty(category.subCategories)) {
      setPreviousSelectedCategories(prev => [...prev, selectedCategory])
      setSelectedCategory(category)
    } else {
      showCategory(category?.totalSlug)
    }
  }

  const onCategoryKeyDown = (
    event: React.KeyboardEvent<HTMLLIElement>,
    category: NavMenuCategory
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleCategoryClick(category)()
    }
  }

  const handleBack = () => {
    const previous = previusSelectedCategories.at(-1)

    setPreviousSelectedCategories(prev => prev.slice(0, -1))
    setSelectedCategory(previous || null)
  }

  const showCategory = (slug: string) => {
    router.push(slug)
    handleClose()
  }

  const mainCategory = useMemo(() => {
    if (selectedCategory && categories) {
      return findMainCategory<NavMenuCategory>(categories, selectedCategory)
    }

    return null
  }, [selectedCategory, categories])

  return (
    <div className={styles.root}>
      <div>
        <button
          className={styles.buttonWrapper}
          aria-expanded={open}
          aria-controls='categories-navigation-mobile'
          aria-haspopup='dialog'
          onClick={open ? handleClose : handleOpen}
        >
          <IconMenu2 color={theme.colors.brand.secondary} />
          Kategorie
        </button>
      </div>
      <Drawer
        id='categories-navigation-mobile'
        placement='left'
        open={open}
        onClose={handleClose}
        {...addTestId('drawer-menu-mobile')}
      >
        <div className={styles.drawerHeader}>
          {selectedCategory ? (
            <Button
              className={styles.backButton}
              onClick={handleBack}
              size='small'
              variant='neutralText'
              startIcon={<IconArrowNarrowLeft />}
              {...addTestId('back-category-button-mobile')}
            >
              Powrót do poprzedniej kategorii
            </Button>
          ) : (
            <DrawerTitle>Wybierz kategorię</DrawerTitle>
          )}
        </div>

        <nav className={styles.column}>
          {selectedCategory ? (
            <>
              <div className={styles.header}>
                <div className={styles.categoryIconWrapper}>
                  <Image
                    alt={mainCategory?.name || 'Kategoria'}
                    src={mainCategory?.icon?.path || ''}
                    width={24}
                    height={24}
                  />
                </div>
                <div className={styles.mobileCategoryNameWrapper}>
                  <div className={styles.titleWrapper}>
                    <div className={styles.title}>{selectedCategory.name}</div>
                    <div className={styles.numberOfProducts}>
                      (
                      {getProperProductForm(
                        selectedCategory?.productsCount || 0
                      )}
                      )
                    </div>
                  </div>
                </div>
              </div>
              <ul className={styles.categoryList} role='menu'>
                <Link
                  className={styles.selectCurrentCategoryButton}
                  href={selectedCategory.totalSlug}
                  onClick={handleClose}
                  role='menuitem'
                  aria-label={`Zobacz całą kategorię ${selectedCategory.name}`}
                >
                  Zobacz całą kategorię
                </Link>

                {selectedCategory?.subCategories?.map(
                  (category: NavMenuCategory) => {
                    return isNotNilOrEmpty(category.subCategories) ? (
                      <li
                        key={category.id}
                        onClick={handleCategoryClick(category)}
                        onKeyDown={event => onCategoryKeyDown(event, category)}
                        className={clsx(styles.categoryItem, {
                          [styles.active]: isActive(pathname, category)
                        })}
                        role='menuitem'
                        aria-haspopup={isNotNilOrEmpty(category.subCategories)}
                        aria-current={
                          selectedCategory?.id === category.id
                            ? 'page'
                            : undefined
                        }
                        aria-expanded={selectedCategory?.id === category.id}
                        {...addTestId('subcategory-menu-item-mobile')}
                        tabIndex={0}
                      >
                        <div className={styles.categoryItemLabel}>
                          {category.name}
                        </div>
                        <IconChevronRight />
                      </li>
                    ) : (
                      <Link
                        key={category.id}
                        href={category.totalSlug}
                        className={clsx(styles.categoryItem, {
                          [styles.active]: isActive(pathname, category)
                        })}
                        onClick={handleClose}
                        role='menuitem'
                        aria-label={`Zobacz całą kategorię ${category.name}`}
                      >
                        {category.name}
                      </Link>
                    )
                  }
                )}
              </ul>
            </>
          ) : (
            <ul className={styles.categoryList} role='menu'>
              <li className={styles.categoryItemWrapper} role='none'>
                <Link
                  href={PATHS.products}
                  className={clsx(
                    styles.categoryItem,
                    styles.categoryItemAllProducts,
                    { [styles.active]: pathname === PATHS.products }
                  )}
                  role='menuitem'
                  onClick={handleClose}
                  {...addTestId('subcategory-menu-item-mobile')}
                  tabIndex={0}
                >
                  <div className={styles.categoryItemLabel}>
                    <IconCategory
                      width={24}
                      height={24}
                      color={cssColors.brand.secondary}
                    />
                    Wszystkie produkty
                  </div>
                </Link>
              </li>
              {categories?.map((category: NavMenuCategory) => {
                const icon = category.icon?.path || '/static/question-mark.svg'

                if (!category.isVisible) {
                  return null
                }

                return isNotNilOrEmpty(category.subCategories) ? (
                  <li
                    key={category.id}
                    onClick={handleCategoryClick(category)}
                    onKeyDown={event => onCategoryKeyDown(event, category)}
                    className={clsx(styles.categoryItem, {
                      [styles.active]: isActive(pathname, category)
                    })}
                    role='menuitem'
                    aria-haspopup={isNotNilOrEmpty(category.subCategories)}
                    aria-expanded={false}
                    {...addTestId('category-menu-item-mobile')}
                    tabIndex={0}
                  >
                    <div className={styles.categoryItemLabel}>
                      <Image
                        src={icon}
                        width={24}
                        height={24}
                        alt={category.name}
                      />
                      {category.name}
                    </div>
                    {isNotNilOrEmpty(category.subCategories) && (
                      <IconChevronRight />
                    )}
                  </li>
                ) : (
                  <Link
                    key={category.id}
                    href={category.totalSlug}
                    onClick={handleClose}
                    className={clsx(styles.categoryItem, {
                      [styles.active]: isActive(pathname, category)
                    })}
                    role='menuitem'
                  >
                    <div className={styles.categoryItemLabel}>
                      <Image
                        src={icon}
                        width={24}
                        height={24}
                        alt={category.name}
                      />
                      {category.name}
                    </div>
                  </Link>
                )
              })}
            </ul>
          )}
        </nav>
      </Drawer>
    </div>
  )
}

export default CategoriesNavigationMobile
